/**
 * Where the customer's orders have got to (snippets/cw-tracking-data.liquid): the tracking
 * strip over the aisles (<cw-track>, phones and tablets) and Account's order cards
 * (assets/cw-account.js). design-handoff/specs/order-tracking-spec.md §4–6,
 * components/order-tracking.md; the words are Shopping v13's (its trk / acc view-model).
 *
 * Each order carries `t`, the `chakki.tracking` note Flow keeps on the Shopify order:
 * {kind, stage, issue, milled, by, moved, at{...}}. What the shopper sees:
 *   head  "Arriving today before 3pm" (express "by"), "Delivered at 12:30pm", "Couldn’t
 *         deliver"; a pick-up reads "Pick up at our shop" / "Ready at our shop" / "Picked up".
 *   tag   Order confirmed · Being milled (Being prepared when nothing is milled) · Packed ·
 *         Out for delivery · Delivered · Ready for pick-up · Picked up · Running late ·
 *         Needs a new time.
 * Late is worked out here as well as in Flow, so the strip turns the minute the promise
 * passes. A late order whose new time the shop has set reads "Now arriving …"; with no new
 * time it says where it is ("Out for delivery") and the tag says it is late: there is no
 * time to promise until somebody sets one.
 *
 * Which orders: the last three days', not cancelled; a delivered one for two hours after it
 * arrived (spec §5). An order Flow has not written about yet is "Order confirmed" for its
 * first twelve hours, and nothing after. Two or more: the strip shows the soonest and opens
 * Account, which lists them.
 */
const DAY = 86400000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
const ON_THE_WAY = ['confirmed', 'milling', 'packed', 'out'];

const read = (id) => {
  try {
    return JSON.parse(document.getElementById(id)?.textContent || 'null');
  } catch {
    return null;
  }
};

/** Flow's clock: "3pm", "11:30am". */
function clock(date) {
  const m = date.getMinutes();
  const h = date.getHours() % 12 || 12;
  const mer = date.getHours() < 12 ? 'am' : 'pm';
  return m ? `${h}:${String(m).padStart(2, '0')}${mer}` : `${h}${mer}`;
}

/** "today", "tomorrow", "yesterday", else "3 Oct". */
function day(date, now) {
  const a = new Date(now);
  a.setHours(0, 0, 0, 0);
  const b = new Date(date);
  b.setHours(0, 0, 0, 0);
  const diff = Math.round((b - a) / DAY);
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff === -1) return 'yesterday';
  return `${b.getDate()} ${MONTHS[b.getMonth()]}`;
}

const rupees = (paise) => `₹${(Math.round(paise) / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

/** One order as the strip and Account show it, or null when there is nothing to show. */
export function describe(order, now = Date.now()) {
  const placed = order.at * 1000;
  let t = order.t;
  if (!t) {
    // Flow writes within minutes of landing; until then all that is true is "confirmed".
    if (now - placed > DAY / 2) return null;
    t = { stage: 'confirmed', kind: 'delivery', fresh: true };
  }
  if (order.cancelled || t.stage === 'cancelled') return null;

  const pickup = t.kind === 'pickup';
  const at = t.at || {};
  const by = t.by ? new Date(t.by) : null;
  const failed = t.issue === 'failed';
  const onTheWay = ON_THE_WAY.includes(t.stage);
  const passed = !pickup && onTheWay && by && now > by.getTime();
  const newTime = !pickup && onTheWay && t.moved && by && !passed;
  const late = !failed && (t.issue === 'late' || passed || newTime);

  const stageWord = {
    confirmed: 'Order confirmed',
    milling: t.milled === false ? 'Being prepared' : 'Being milled',
    packed: 'Packed',
    out: 'Out for delivery',
    delivered: 'Delivered',
    ready: 'Ready for pick-up',
    picked: 'Picked up',
  }[t.stage];
  if (!stageWord) return null;
  const tag = failed ? 'Needs a new time' : late ? 'Running late' : stageWord;

  let head;
  if (pickup && !failed) head = t.stage === 'picked' ? 'Picked up' : t.stage === 'ready' ? 'Ready at our shop' : 'Pick up at our shop';
  else if (failed) head = 'Couldn’t deliver';
  else if (t.stage === 'delivered') {
    const when = at.delivered ? new Date(at.delivered) : null;
    head = when ? `Delivered ${day(when, now) === 'today' ? '' : `${day(when, now)} `}at ${clock(when)}` : 'Delivered';
  } else if (newTime) head = `Now arriving ${day(by, now)} by ${clock(by)}`;
  else if (late || t.fresh || !by) head = stageWord;
  else head = `Arriving ${day(by, now)} ${t.kind === 'express' ? 'by' : 'before'} ${clock(by)}`;

  const icon = failed
    ? 'fail'
    : late
      ? 'late'
      : pickup && t.stage === 'ready'
        ? 'store'
        : t.stage === 'picked'
          ? 'delivered'
          : t.stage;

  // On show: the last three days, not finished; delivered, for two hours.
  let active = now - placed < 3 * DAY && !['delivered', 'picked'].includes(t.stage);
  if (t.stage === 'delivered' && at.delivered) active = now - new Date(at.delivered).getTime() < 2 * 3600000;

  const placedOn = new Date(placed);
  return {
    name: order.name,
    url: order.url,
    head,
    tag,
    icon,
    tone: failed ? 'fail' : late ? 'late' : 'ok',
    meta: `${order.name} · ${placedOn.getDate()} ${MONTHS[placedOn.getMonth()]} · ${order.n} item${order.n === 1 ? '' : 's'} · ${rupees(order.total)}`,
    active,
    soon: by ? by.getTime() : Infinity,
  };
}

/** The orders to show now, soonest first. */
export function activeOrders(now = Date.now()) {
  return (read('cw-tracking') || [])
    .map((order) => describe(order, now))
    .filter((d) => d && d.active)
    .sort((a, b) => a.soon - b.soon);
}

let icons = null;
/** The state icon (the windmill turns), from the snippet's template. */
export function iconHtml(name) {
  if (!icons) {
    icons = {};
    const box = document.querySelector('template[data-cw="track-icons"]');
    for (const el of box?.content.querySelectorAll('[data-icon]') || []) icons[el.dataset.icon] = el.innerHTML.trim();
  }
  return icons[name] || icons.confirmed || '';
}

const esc = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/**
 * <cw-track>: the strip (snippets/cw-shell-bottom). Over the aisles and Home; not on the
 * product page, search or the basket page, and out of the way while a panel or screen is up
 * (assets/cw-shell.css). It looks again every minute: an order turns late, or a delivered
 * one's two hours run out, without a page load.
 */
class CwTrack extends HTMLElement {
  #timer = 0;

  connectedCallback() {
    this.link = this.querySelector('a');
    this.link?.addEventListener('click', this.#onClick);
    this.#paint();
    this.#timer = setInterval(() => {
      if (!document.hidden) this.#paint();
    }, 60000);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
  }

  #paint() {
    const list = activeOrders();
    this.list = list;
    const top = list[0];
    this.hidden = !top;
    if (!top) return;
    this.querySelector('[data-cw="icon"]').innerHTML = iconHtml(top.icon);
    this.querySelector('[data-cw="head"]').textContent = top.head;
    this.querySelector('[data-cw="sub"]').textContent = `${top.tag} · ${list.length > 1 ? `+${list.length - 1} more` : top.name}`;
    this.link.href = list.length > 1 ? `${window.Shopify?.routes?.root || '/'}account` : top.url;
    this.link.setAttribute('aria-label', `${top.head}. ${top.tag}, order ${top.name}${list.length > 1 ? `, and ${list.length - 1} more` : ''}`);
  }

  /** Two or more orders: Account lists them (spec §5). */
  #onClick = (event) => {
    if ((this.list || []).length < 2) return;
    const ask = new CustomEvent('cw:account:open', { cancelable: true, detail: { source: this.link } });
    document.dispatchEvent(ask);
    if (ask.defaultPrevented) event.preventDefault();
  };
}

if (!customElements.get('cw-track')) customElements.define('cw-track', CwTrack);

export { esc };
